import update from 'immutability-helper';
import camelCase from 'lodash/camelCase';
import isFunction from 'lodash/isFunction';
import {
  FAILED,
  SUCCEEDED
} from './constants';
import { objectValues } from './utils';

const prependNamespace = (namespace, type) => (
  namespace ? `${namespace}/${type}` : type
);

const buildAction = (type = '', namespace, payloadCreator, metaCreator) => {
  const hasPayload = isFunction(payloadCreator);
  const hasMeta = isFunction(metaCreator);
  const typeString = type.toString();
  const finalPayloadCreator = (head, ...args) => (head instanceof Error
    ? head : (hasPayload ? payloadCreator(head, ...args) : undefined));

  const finalMetaCreator = args => (hasMeta ? metaCreator(args) : undefined);

  const actionCreator = (...args) => {
    const action = {
      type: prependNamespace(namespace, type)
    };
    const meta = finalMetaCreator(...args);
    const payload = finalPayloadCreator(...args);

    if (payload instanceof Error) {
      action.error = true;
    }

    if (payload !== undefined) {
      action.payload = payload;
    }

    if (meta !== undefined) {
      action.meta = meta;
    }
    return action;
  };
  actionCreator.toString = () => typeString;
  return actionCreator;
};

export const buildActionTypes = (type, { failed: { include: failed = true } = {}, succeeded: { include: succeeded = true } = {} } = {}) => {
  const initialOptions = {
    failed: failed && true,
    succeeded: succeeded && true
  };
  const types = {
    original: type
  };
  Object.keys(initialOptions).forEach((key) => {
    let name;
    const include = initialOptions[key];
    if (include) {
      switch (key) {
        case 'failed':
          name = `${type}_${FAILED}`;
          break;
        case 'succeeded':
          name = `${type}_${SUCCEEDED}`;
          break;
        default:
          break;
      }
      types[key] = name;
    }
  });
  return types;
};

const actionTypesWithOptions = (type, options = {}) => {
  const types = [];
  const initialValues = {
    include: true,
    metaCreator: undefined,
    payloadCreator: undefined
  };
  const { include, ...creators } = initialValues;
  let initialOptions = {
    failed: { ...initialValues },
    original: { ...creators },
    succeeded: { ...initialValues }
  };
  initialOptions = update(initialOptions, {
    failed: { $merge: { ...options.failed } },
    original: { $merge: { ...options.original } },
    succeeded: { $merge: { ...options.succeeded } }
  });
  const actionTypes = buildActionTypes(type, {
    failed: { include: initialOptions.failed.include },
    succeeded: { include: initialOptions.succeeded.include }
  });
  Object.keys(actionTypes).forEach((key) => {
    const {
      metaCreator,
      payloadCreator
    } = initialOptions[key];
    types.push({
      metaCreator,
      payloadCreator,
      type: actionTypes[key]
    });
  });
  return types;
};

export const createActions = (typeName, namespace, options = {}) => (
  actionTypesWithOptions(typeName, options).reduce((acc, { type, metaCreator, payloadCreator }) => {
    acc[`${camelCase(type)}`] = buildAction(type, namespace, payloadCreator, metaCreator);
    return acc;
  }, {})
);

export const createActionTypes = (typeName, namespace, options = {}) => {
  const actionTypes = buildActionTypes(typeName, options);
  const types = {};
  objectValues(actionTypes).forEach((type) => {
    types[`${camelCase(type)}`] = prependNamespace(namespace, type);
  });
  return types;
};



// WEBPACK FOOTER //
// ./src/shared/actions.js